import { useEffect } from 'react'
import type { UseCopilotChatResult } from '@/hooks/useCopilotChat'
import { LoadingState } from '@/components/common/LoadingState'
import { ErrorState } from '@/components/common/ErrorState'
import { ChatHeader } from './ChatHeader'
import { ChatCanvas } from './ChatCanvas'
import { WelcomeCanvas } from './WelcomeCanvas'
import { PromptComposer } from './PromptComposer'
import { ConversationHistory } from './ConversationHistory'
import styles from './ChatWorkspace.module.css'

interface ChatWorkspaceProps {
  chat: UseCopilotChatResult
}

export function ChatWorkspace({ chat }: ChatWorkspaceProps) {
  const {
    status,
    messages,
    error,
    isBusy,
    hasStarted,
    conversations,
    activeConversationId,
    persistenceEnabled,
    connect,
    sendMessage,
    newConversation,
    selectConversation,
    removeConversation,
    retry,
  } = chat

  useEffect(() => {
    if (!hasStarted) {
      void connect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isInitialConnecting = status === 'connecting' && messages.length === 0
  const isInitialError = status === 'error' && messages.length === 0

  return (
    <div className={styles.root}>
      {persistenceEnabled && (
        <ConversationHistory
          conversations={conversations}
          activeConversationId={activeConversationId}
          disabled={isBusy}
          onNew={() => void newConversation()}
          onSelect={(id) => void selectConversation(id)}
          onDelete={(id) => void removeConversation(id)}
        />
      )}
      <div className={styles.conversation}>
        <ChatHeader />
        <div className={styles.body}>
          {isInitialConnecting ? (
            <div className={styles.centered}>
              <LoadingState
                label="Se conectează la SOC-Copilot…"
                hint="Se stabilește o sesiune securizată cu agentul de investigație."
              />
            </div>
          ) : isInitialError && error ? (
            <div className={styles.centered}>
              <ErrorState title={error.title} detail={error.detail} onRetry={retry} retryLabel="Reîncearcă conectarea" />
            </div>
          ) : messages.length === 0 ? (
            <WelcomeCanvas onSelectPrompt={sendMessage} />
          ) : (
            <ChatCanvas messages={messages} isBusy={isBusy} />
          )}
        </div>
        {!isInitialConnecting && !isInitialError && (
          <>
            {error && messages.length > 0 && (
              <div className={styles.inlineError}>
                <ErrorState title={error.title} detail={error.detail} onRetry={retry} />
              </div>
            )}
            <PromptComposer onSend={sendMessage} disabled={isBusy || status === 'idle'} />
          </>
        )}
      </div>
    </div>
  )
}
